// Canonical demo dataset — one fabricated brokerage, shared by every
// enterprise that switches "Testing mode" on. Nothing here is ever
// written to a database; both apps read it and project it into their
// own response shapes:
//   - apps/manager  → manager list-response shapes (lib/testFixtures.ts)
//   - apps/landing  → public proptree shapes (lib/demoProptree.ts)
// Keep this file free of app-specific types so both can import it.

export const IMG = (id: string) =>
  `https://images.unsplash.com/photo-${id}?auto=format&fit=crop&w=600&q=60`;

export const LISTING_IMAGES = [
  IMG('1512917774080-9991f1c4c750'),
  IMG('1580587771525-78b9dba3b914'),
  IMG('1522708323590-d24dbb6b0267'),
  IMG('1600585154340-be6161a56a0c'),
  IMG('1600596542815-ffad4c1539a9'),
  IMG('1600607687939-ce8a6c25118c'),
  IMG('1600607687920-4e2a09cf159d'),
  IMG('1600566753086-00f18fb6b3ea'),
  IMG('1600585154526-990dced4db0d'),
  IMG('1600573472592-401b489a3cdc'),
  IMG('1600047509807-ba8f99d2cdde'),
  IMG('1600585152220-90363fe7e115'),
  IMG('1600210492486-724fe5c67fb0'),
  IMG('1600566752355-35792bedcfea'),
  IMG('1600607688969-a5bfcd646154'),
  IMG('1600121848594-d8644e57abab'),
  IMG('1613490493576-7fde63acd811'),
  IMG('1613977257363-707ba9348227'),
  IMG('1613977257592-4871e5fcd7c4'),
  IMG('1582268611958-ebfd161ef9cf'),
  IMG('1560448204-e02f11c3d0e2'),
  IMG('1560185007-cde436f6a4d0'),
  IMG('1560185127-6ed189bf02f4'),
  IMG('1502672260266-1c1ef2d93688'),
  IMG('1493809842364-78817add7ffb'),
  IMG('1484154218962-a197022b5858'),
  IMG('1556912167-f556f1f39fdf'),
  IMG('1554995207-c18c203602cb'),
];

// Stock demo headshots (stable randomuser.me portrait URLs, same idea as
// the Unsplash listing photos above).
export const FACE = (folder: 'men' | 'women', n: number) =>
  `https://randomuser.me/api/portraits/${folder}/${n}.jpg`;

export const AGENTS = [
  { id: 'test-agent-1', name: 'Sarah Mitchell', email: 'sarah@example-demo.com', phone: '+971 50 000 0001', alias: 'sarah-mitchell', image: FACE('women', 44) },
  { id: 'test-agent-2', name: 'Omar Haddad', email: 'omar@example-demo.com', phone: '+971 50 000 0002', alias: 'omar-haddad', image: FACE('men', 32) },
  { id: 'test-agent-3', name: 'Elena Petrova', email: 'elena@example-demo.com', phone: '+971 50 000 0003', alias: 'elena-petrova', image: FACE('women', 65) },
  { id: 'test-agent-4', name: 'James Carter', email: 'james@example-demo.com', phone: '+971 50 000 0004', alias: 'james-carter', image: FACE('men', 22) },
  { id: 'test-agent-5', name: 'Priya Nair', email: 'priya@example-demo.com', phone: '+971 50 000 0005', alias: 'priya-nair', image: FACE('women', 68) },
  { id: 'test-agent-6', name: 'Layla Rahman', email: 'layla@example-demo.com', phone: '+971 50 000 0006', alias: 'layla-rahman', image: FACE('women', 12) },
  { id: 'test-agent-7', name: 'Marcus Boye', email: 'marcus@example-demo.com', phone: '+971 50 000 0007', alias: 'marcus-boye', image: FACE('men', 75) },
  { id: 'test-agent-8', name: 'Aisha Khan', email: 'aisha@example-demo.com', phone: '+971 50 000 0008', alias: 'aisha-khan', image: FACE('women', 26) },
  { id: 'test-agent-9', name: 'Tom Beckett', email: 'tom@example-demo.com', phone: '+971 50 000 0009', alias: 'tom-beckett', image: FACE('men', 41) },
  { id: 'test-agent-10', name: 'Nadia Salloum', email: 'nadia@example-demo.com', phone: '+971 50 000 0010', alias: 'nadia-salloum', image: FACE('women', 51) },
  { id: 'test-agent-11', name: 'Georgi Dimitrov', email: 'georgi@example-demo.com', phone: '+971 50 000 0011', alias: 'georgi-dimitrov', image: FACE('men', 64) },
  { id: 'test-agent-12', name: 'Chloe Martin', email: 'chloe@example-demo.com', phone: '+971 50 000 0012', alias: 'chloe-martin', image: FACE('women', 33) },
  { id: 'test-agent-13', name: 'Rashid Al Farsi', email: 'rashid@example-demo.com', phone: '+971 50 000 0013', alias: 'rashid-al-farsi', image: FACE('men', 18) },
  { id: 'test-agent-14', name: 'Mei Lin', email: 'mei@example-demo.com', phone: '+971 50 000 0014', alias: 'mei-lin', image: FACE('women', 79) },
  { id: 'test-agent-15', name: 'Daniel Okafor', email: 'daniel@example-demo.com', phone: '+971 50 000 0015', alias: 'daniel-okafor', image: FACE('men', 86) },
];

// Per-agent demo stats, index-aligned with AGENTS. Kept in one place so
// the agent list, proptree list, and dashboard rankings all tell the same
// story (clicks/leads descend with a few newer agents still at zero).
export const AGENT_LEADS =    [31, 24, 17, 9, 4, 8, 6, 5, 3, 2, 2, 1, 1, 0, 0];
export const AGENT_LISTINGS = [12, 9, 7, 5, 2, 6, 8, 4, 3, 5, 2, 3, 1, 2, 1];
export const AGENT_LISTING_INQ = [22, 18, 12, 6, 3, 6, 4, 4, 2, 1, 1, 1, 0, 0, 0];
export const AGENT_PAGE_VIEWS = [1450, 1120, 860, 420, 180, 640, 510, 380, 290, 210, 160, 120, 95, 60, 40];
export const AGENT_LISTING_CLICKS = [412, 356, 288, 130, 61, 190, 154, 118, 92, 71, 48, 36, 22, 14, 8];
export const AGENT_BUTTON_CLICKS = [96, 72, 44, 21, 8, 33, 27, 19, 14, 11, 7, 5, 3, 2, 1];
// Fake RERA-style broker permit numbers (shown in the Permit column).
export const AGENT_PERMITS = ['27114', '30258', '24967', '33741', '28502', '35619', '26873', '31485', '29736', '34028', '25391', '32647', '27958', '36104', '30875'];
// Account-recovery short codes the agent quotes to restore their page.
// Real ones (legacy `restoreCode`) are 7-digit numeric strings — kept as
// strings so leading zeros survive.
export const AGENT_RECOVERY_CODES = ['4820193', '0459096', '3303744', '9308201', '1255377', '6898038', '0182469', '5179837', '4277687', '2846015', '7031928', '0664152', '3915470', '8207361', '1548803'];
// Internal unique proptree short-ids (shown under the @alias, and the
// public URL fallback when no alias is set). Same format as the landing
// demo's: 'p' + 5 lowercase alphanumerics.
export const PROPTREE_SHORT_IDS = ['p7f3k2', 'p2m8x9', 'p5q1d7', 'p3v6n8', 'p8k2w4', 'p4j7r5', 'p1x5b6', 'p9t4c3', 'p6d8h1', 'p2r5m7', 'p8w1y4', 'p5n3j9', 'p1k7v2', 'p4b6q8', 'p9g2z5'];
// Where each agent's proptree link is confirmed to live ("Link in bio"
// column) — normally detected from per-channel organic page loads.
// Empty array = link not detected anywhere yet ("Unconfirmed" pill).
// Only channels with a brand icon asset (/social/*.png) are used so the
// demo always shows real logos.
export const AGENT_BIO_CHANNELS: string[][] = [
  ['instagram', 'linkedin'],
  ['facebook', 'instagram'],
  ['instagram'],
  ['linkedin'],
  ['instagram', 'tiktok'],
  ['instagram', 'snapchat'],
  ['whatsapp'],
  ['facebook'],
  ['linkedin'],
  ['instagram'],
  ['facebook', 'whatsapp'],
  ['instagram', 'tiktok'],
  [],
  ['tiktok'],
  [],
];


export const LISTING_SEED = [
  { title: '2 Bed Apartment · Marina Promenade', price: 2_850_000, bed: 2, bath: 3, area: 'Dubai Marina', type: 'Apartment', sale: true },
  { title: '4 Bed Villa · Garden District', price: 8_900_000, bed: 4, bath: 5, area: 'Jumeirah', type: 'Villa', sale: true },
  { title: '1 Bed Apartment · City View', price: 118_000, bed: 1, bath: 1, area: 'Downtown', type: 'Apartment', sale: false },
  { title: '3 Bed Townhouse · Waterfront Row', price: 5_150_000, bed: 3, bath: 3, area: 'Creek Harbour', type: 'Townhouse', sale: true },
  { title: 'Penthouse · Skyline Terrace', price: 18_500_000, bed: 4, bath: 5, area: 'Palm Jumeirah', type: 'Penthouse', sale: true },
  { title: 'Studio · Design Quarter', price: 74_000, bed: 0, bath: 1, area: 'Design District', type: 'Studio', sale: false },
  { title: '3 Bed Apartment · Bay Vista', price: 3_950_000, bed: 3, bath: 4, area: 'Business Bay', type: 'Apartment', sale: true },
  { title: '1 Bed Apartment · Lake Terrace', price: 92_000, bed: 1, bath: 2, area: 'JLT', type: 'Apartment', sale: false },
  { title: '5 Bed Villa · Hills Grove', price: 14_200_000, bed: 5, bath: 6, area: 'Dubai Hills', type: 'Villa', sale: true },
  { title: '2 Bed Apartment · Beachfront Residences', price: 210_000, bed: 2, bath: 2, area: 'JBR', type: 'Apartment', sale: false },
  { title: '4 Bed Townhouse · Maple Row', price: 4_350_000, bed: 4, bath: 4, area: 'Dubai Hills', type: 'Townhouse', sale: true },
  { title: 'Studio · Circle Heights', price: 620_000, bed: 0, bath: 1, area: 'JVC', type: 'Studio', sale: true },
  { title: '2 Bed Apartment · Boulevard Central', price: 3_150_000, bed: 2, bath: 3, area: 'Downtown', type: 'Apartment', sale: true },
  { title: '3 Bed Villa · Springs Meadow', price: 240_000, bed: 3, bath: 3, area: 'The Springs', type: 'Villa', sale: false },
  { title: '1 Bed Apartment · City Walk Central', price: 1_850_000, bed: 1, bath: 2, area: 'City Walk', type: 'Apartment', sale: true },
  { title: '6 Bed Mansion · Emirates Hills Estate', price: 32_000_000, bed: 6, bath: 8, area: 'Emirates Hills', type: 'Villa', sale: true },
  { title: '2 Bed Loft · DIFC Skyline', price: 165_000, bed: 2, bath: 2, area: 'DIFC', type: 'Apartment', sale: false },
  { title: '4 Bed Villa · Ranches Retreat', price: 6_800_000, bed: 4, bath: 5, area: 'Arabian Ranches', type: 'Villa', sale: true },
  { title: 'Penthouse · Bluewaters Horizon', price: 24_500_000, bed: 5, bath: 6, area: 'Bluewaters', type: 'Penthouse', sale: true },
  { title: 'Studio · Motor City Uptown', price: 48_000, bed: 0, bath: 1, area: 'Motor City', type: 'Studio', sale: false },
  { title: '3 Bed Duplex · Meydan Views', price: 2_650_000, bed: 3, bath: 3, area: 'Meydan', type: 'Duplex', sale: true },
  { title: '2 Bed Apartment · Creek Rise', price: 145_000, bed: 2, bath: 2, area: 'Creek Harbour', type: 'Apartment', sale: false },
  { title: '4 Bed Townhouse · Damac Hills Park', price: 3_750_000, bed: 4, bath: 4, area: 'Damac Hills', type: 'Townhouse', sale: true },
  { title: '1 Bed Apartment · Sports City Arena', price: 58_000, bed: 1, bath: 1, area: 'Sports City', type: 'Apartment', sale: false },
  { title: '3 Bed Apartment · Palm Shoreline', price: 7_900_000, bed: 3, bath: 4, area: 'Palm Jumeirah', type: 'Apartment', sale: true },
  { title: '5 Bed Villa · Al Barsha Oasis', price: 380_000, bed: 5, bath: 6, area: 'Al Barsha', type: 'Villa', sale: false },
  { title: '2 Bed Apartment · Greens Panorama', price: 1_950_000, bed: 2, bath: 2, area: 'The Greens', type: 'Apartment', sale: true },
  { title: '3 Bed Townhouse · Town Square Parkside', price: 2_350_000, bed: 3, bath: 4, area: 'Town Square', type: 'Townhouse', sale: true },
  { title: '1 Bed Apartment · Silicon Gate', price: 46_000, bed: 1, bath: 1, area: 'Dubai Silicon Oasis', type: 'Apartment', sale: false },
  { title: '4 Bed Villa · Golf Estates Fairway', price: 11_500_000, bed: 4, bath: 5, area: 'Jumeirah Golf Estates', type: 'Villa', sale: true },
  { title: 'Studio · Discovery Gardens Court', price: 38_000, bed: 0, bath: 1, area: 'Discovery Gardens', type: 'Studio', sale: false },
  { title: '3 Bed Apartment · La Mer Seafront', price: 5_600_000, bed: 3, bath: 3, area: 'La Mer', type: 'Apartment', sale: true },
  { title: '2 Bed Townhouse · Al Furjan Grove', price: 148_000, bed: 2, bath: 3, area: 'Al Furjan', type: 'Townhouse', sale: false },
  { title: '5 Bed Villa · Tilal Al Ghaf Lagoon', price: 9_800_000, bed: 5, bath: 6, area: 'Tilal Al Ghaf', type: 'Villa', sale: true },
  { title: '1 Bed Apartment · Barsha Heights Loft', price: 72_000, bed: 1, bath: 1, area: 'Barsha Heights', type: 'Apartment', sale: false },
  { title: '3 Bed Duplex · Madinat Living', price: 4_900_000, bed: 3, bath: 4, area: 'Madinat Jumeirah Living', type: 'Duplex', sale: true },
  { title: '2 Bed Apartment · Views Golf Vista', price: 2_250_000, bed: 2, bath: 2, area: 'The Views', type: 'Apartment', sale: true },
  { title: '4 Bed Villa · Green Community West', price: 320_000, bed: 4, bath: 4, area: 'Green Community', type: 'Villa', sale: false },
  { title: 'Studio · International City Cluster', price: 26_000, bed: 0, bath: 1, area: 'International City', type: 'Studio', sale: false },
  { title: '3 Bed Villa · Mirdif Courtyard', price: 2_850_000, bed: 3, bath: 4, area: 'Mirdif', type: 'Villa', sale: true },
  { title: '2 Bed Apartment · Expo Valley', price: 1_650_000, bed: 2, bath: 2, area: 'Expo City', type: 'Apartment', sale: true },
  { title: '1 Bed Apartment · Dubai South Gateway', price: 750_000, bed: 1, bath: 1, area: 'Dubai South', type: 'Apartment', sale: true },
  { title: '4 Bed Townhouse · Nad Al Sheba Gardens', price: 5_900_000, bed: 4, bath: 5, area: 'Nad Al Sheba', type: 'Townhouse', sale: true },
  { title: '2 Bed Apartment · Island Bay', price: 195_000, bed: 2, bath: 2, area: 'Dubai Islands', type: 'Apartment', sale: false },
  { title: '3 Bed Townhouse · Dubailand Meadows', price: 165_000, bed: 3, bath: 3, area: 'Dubailand', type: 'Townhouse', sale: false },
  { title: 'Penthouse · Umm Suqeim Crest', price: 13_800_000, bed: 4, bath: 5, area: 'Umm Suqeim', type: 'Penthouse', sale: true },
];

// Per-listing lead counts, index-aligned with LISTING_SEED (sums to ~112,
// in the same ballpark as the agents' lead totals).
export const LISTING_LEADS = [
  14, 11, 8, 6, 4, 2, 7, 5, 6, 4, 3, 5, 2, 4, 3, 2, 3, 1, 2, 2, 1, 3, 1,
  0, 2, 1, 1, 0, 1, 2, 0, 1, 0, 0, 1, 0, 2, 0, 1, 0, 0, 1, 0, 0, 0, 0,
];

export const AFFILIATES = [
  { id: 'test-aff-1', alias: 'instagram-bio', company: 'Instagram Bio', first: 'Instagram', status: 'enabled', buttons: 2, active: 2, views: 3420, btnClicks: 512, refClicks: 204, created: '2026-02-11', channels: ['instagram'] },
  { id: 'test-aff-2', alias: 'marina-open-house', company: 'Marina Open House', first: 'Marina', status: 'enabled', buttons: 4, active: 3, views: 2140, btnClicks: 310, refClicks: 128, created: '2026-03-08', channels: ['instagram', 'whatsapp'] },
  { id: 'test-aff-3', alias: 'q3-portal-campaign', company: 'Q3 Portal Campaign', first: 'Portal', status: 'enabled', buttons: 5, active: 5, views: 1680, btnClicks: 245, refClicks: 96, created: '2026-04-02', channels: ['facebook', 'instagram'] },
  { id: 'test-aff-4', alias: 'property-expo-2026', company: 'Property Expo 2026', first: 'Expo', status: 'enabled', buttons: 3, active: 2, views: 940, btnClicks: 118, refClicks: 41, created: '2026-05-15', channels: ['linkedin'] },
  { id: 'test-aff-5', alias: 'ramadan-promo', company: 'Ramadan Promo', first: 'Ramadan', status: 'disabled', buttons: 3, active: 0, views: 460, btnClicks: 38, refClicks: 12, created: '2026-02-20', channels: [] },
];

export const AFF_BUTTON_TITLES = ['View listings', 'WhatsApp us', 'Book a viewing', 'Download brochure', 'Follow on Instagram'];
export const AFF_BUTTON_ICONS: Array<string | null> = ['🏠', null, '📅', '📄', '📸'];


// --- Forms -----------------------------------------------------------
// Raw form definitions. Each app builds its own shape from these: the
// manager renders them in its forms list/editor, and the landing app
// serves them from /api/forms/public/:id so the listing popup's inquiry
// form actually fills in during a test-mode preview.
export interface DemoFormDef {
  id: string;
  name: string;
  questions: { q: string; opts: string[] }[];
  stats: { opened: number; started: number; finished: number };
}

export const DEMO_FORM_DEFS: DemoFormDef[] = [
  {
    id: 'test-form-default',
    name: 'Default inquiry form',
    questions: [
      { q: 'What are you looking for?', opts: ['Buying', 'Renting', 'Just browsing'] },
      { q: 'What is your budget?', opts: ['Under AED 1M', 'AED 1–5M', 'Above AED 5M'] },
      { q: 'When do you want to move?', opts: ['As soon as possible', 'Within 3 months', 'Later this year'] },
    ],
    // Funnel narrows opened → started → finished; finished matches the
    // dashboard's all-time inquiry count (512).
    stats: { opened: 1240, started: 861, finished: 512 },
  },
  {
    id: 'test-form-viewing',
    name: 'Viewing request',
    questions: [
      { q: 'Preferred viewing time?', opts: ['Weekday daytime', 'Weekday evening', 'Weekend'] },
      { q: 'How should we confirm?', opts: ['WhatsApp', 'Phone call', 'Email'] },
    ],
    stats: { opened: 486, started: 342, finished: 198 },
  },
];

// Question list in the shape both the manager `Form` and the landing
// public-form endpoint expect. First option is the qualifying one.
export function demoFormQuestions(def: DemoFormDef) {
  return def.questions.map((q, i) => ({
    id: i + 1,
    question: q.q,
    information: '',
    options: q.opts.map((title, j) => ({
      id: j + 1,
      title,
      qualification: j === 0,
    })),
    answerType: { id: 1, name: 'Options', value: 'options' },
  }));
}

export function findDemoForm(id: string): DemoFormDef | undefined {
  return DEMO_FORM_DEFS.find((f) => f.id === id);
}

// --- Affiliate identity images ---------------------------------------
// Inline SVG avatars, one per demo affiliate. Data URIs rather than
// hosted files so they always render — no network, no broken images, no
// next/image remotePatterns entry — and stay crisp at any size. Each is
// a full-bleed gradient square with a simple white glyph, which crops
// cleanly to the circular avatar the proptree page renders.
const svgAvatar = (inner: string): string =>
  `data:image/svg+xml;utf8,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 128 128">${inner}</svg>`,
  )}`;

const grad = (id: string, from: string, via: string | null, to: string) =>
  `<linearGradient id="${id}" x1="0" y1="0" x2="1" y2="1">` +
  `<stop offset="0" stop-color="${from}"/>` +
  (via ? `<stop offset="0.5" stop-color="${via}"/>` : '') +
  `<stop offset="1" stop-color="${to}"/></linearGradient>`;

// Instagram Bio — camera glyph on the Instagram-style warm gradient.
const AV_INSTAGRAM = svgAvatar(
  `<defs>${grad('g', '#F9A03F', '#DD2A7B', '#8134AF')}</defs>` +
    `<rect width="128" height="128" fill="url(#g)"/>` +
    `<rect x="38" y="38" width="52" height="52" rx="15" fill="none" stroke="#fff" stroke-width="6"/>` +
    `<circle cx="64" cy="64" r="14" fill="none" stroke="#fff" stroke-width="6"/>` +
    `<circle cx="82" cy="46" r="4" fill="#fff"/>`,
);

// Marina Open House — waterfront: sail over waves.
const AV_MARINA = svgAvatar(
  `<defs>${grad('g', '#38BDF8', null, '#0F766E')}</defs>` +
    `<rect width="128" height="128" fill="url(#g)"/>` +
    `<path d="M64 30 L64 74 L36 74 Z" fill="#fff" opacity="0.95"/>` +
    `<path d="M72 46 L90 74 L72 74 Z" fill="#fff" opacity="0.7"/>` +
    `<path d="M26 88 q13 -9 26 0 t26 0 t26 0" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round"/>`,
);

// Q3 Portal Campaign — ascending performance bars.
const AV_PORTAL = svgAvatar(
  `<defs>${grad('g', '#818CF8', null, '#4338CA')}</defs>` +
    `<rect width="128" height="128" fill="url(#g)"/>` +
    `<rect x="34" y="72" width="16" height="26" rx="5" fill="#fff" opacity="0.75"/>` +
    `<rect x="56" y="56" width="16" height="42" rx="5" fill="#fff" opacity="0.88"/>` +
    `<rect x="78" y="34" width="16" height="64" rx="5" fill="#fff"/>`,
);

// Property Expo 2026 — exhibition stand: canopy over a booth.
const AV_EXPO = svgAvatar(
  `<defs>${grad('g', '#FBBF24', null, '#EA580C')}</defs>` +
    `<rect width="128" height="128" fill="url(#g)"/>` +
    `<path d="M30 56 L64 32 L98 56 Z" fill="#fff"/>` +
    `<rect x="44" y="60" width="40" height="36" rx="6" fill="#fff" opacity="0.85"/>` +
    `<rect x="58" y="74" width="12" height="22" rx="3" fill="#EA580C" opacity="0.8"/>`,
);

// Ramadan Promo — crescent and star in gold on deep green. The crescent
// is a masked circle (a filled disc minus an offset disc); two arcs in a
// single path render inconsistently across engines.
const AV_RAMADAN = svgAvatar(
  `<defs>${grad('g', '#047857', null, '#064E3B')}` +
    `<mask id="c">` +
    `<rect width="128" height="128" fill="black"/>` +
    `<circle cx="58" cy="66" r="29" fill="white"/>` +
    `<circle cx="72" cy="58" r="25" fill="black"/>` +
    `</mask></defs>` +
    `<rect width="128" height="128" fill="url(#g)"/>` +
    `<rect width="128" height="128" fill="#F5C451" mask="url(#c)"/>` +
    `<path d="M88 40 l3.2 7 7.6 0.8 -5.6 5.2 1.6 7.6 -6.8 -3.8 -6.8 3.8 1.6 -7.6 -5.6 -5.2 7.6 -0.8 Z" fill="#F5C451"/>`,
);

// Index-aligned with AFFILIATES.
export const AFFILIATE_IMAGES: string[] = [
  AV_INSTAGRAM,
  AV_MARINA,
  AV_PORTAL,
  AV_EXPO,
  AV_RAMADAN,
];

// --- Agency proptree -------------------------------------------------
// The brokerage's own page — the one every agent page inherits from.
// Its avatar follows the same inline-SVG approach as the affiliates: a
// monogram mark on the brand gradient, so it needs no hosted asset.
export const AGENCY = {
  id: 'test-pt-agency',
  alias: 'yourbrokerage',
  shortId: 'p0a1b2',
  company: 'Your Brokerage',
  tagline: 'Dubai property specialists',
  // Agency logo — deliberately a mark on a TRANSPARENT background, not
  // a full-bleed tile. The public page renders an agency avatar with
  // `object-contain` on white (real agency logos are wide wordmarks that
  // a circular cover-crop would slice), so a tile would show up as a
  // square inside the circle. A monoline mark sits correctly on white at
  // every size instead.
  image: svgAvatar(
    `<g fill="none" stroke="#4C3FD1" stroke-width="7" ` +
      `stroke-linecap="round" stroke-linejoin="round">` +
      // Roofline sweeping past the walls, as an estate-agent mark does.
      `<path d="M26 64 L64 32 L102 64"/>` +
      // Walls, open at the top so the roof reads as a separate stroke.
      `<path d="M38 58 L38 98 L90 98 L90 58"/>` +
      // Doorway.
      `<path d="M56 98 L56 76 L72 76 L72 98"/>` +
      `</g>`,
  ),
  buttons: [
    { id: 'agency-btn-1', title: 'Book a valuation', subtitle: 'Free, within 24 hours', url: 'https://calendly.com/yourbrokerage/valuation', icon: '🏠', type: 'link' },
    { id: 'agency-btn-2', title: 'WhatsApp the office', subtitle: null, url: 'https://wa.me/971500000000', icon: null, type: 'whatsapp' },
    { id: 'agency-btn-3', title: 'Meet the team', subtitle: '15 agents across Dubai', url: 'https://example.com/team', icon: '👥', type: 'link' },
    { id: 'agency-btn-4', title: 'Latest brochures', subtitle: null, url: 'https://example.com/brochures', icon: '📄', type: 'link' },
  ],
};

// --- Marketing campaigns ---------------------------------------------
// Column shape mirrors the legacy campaigns list (enterprise-app's
// listCustomCampaigns): listing, name, payment, status, run dates, and
// delivery stats. Statuses match its tabs — pending / running / finished.
export interface DemoCampaign {
  id: string;
  name: string;
  /** What the item is — drives the first column of the marketing list. */
  type: 'post' | 'campaign' | 'feed';
  /** Index into LISTING_SEED — the property being promoted. */
  listing: number;
  status: 'running' | 'pending' | 'finished';
  payment: 'paid' | 'pending' | 'failed';
  start: string;
  end: string;
  impressions: number;
  clicks: number;
  leads: number;
}

export const CAMPAIGNS: DemoCampaign[] = [
  { id: 'test-camp-1', type: 'campaign', name: 'Marina Promenade · Summer push', listing: 0, status: 'running', payment: 'paid', start: '2026-07-01', end: '2026-08-15', impressions: 184_200, clicks: 3120, leads: 48 },
  { id: 'test-camp-2', type: 'campaign', name: 'Garden District villas', listing: 1, status: 'running', payment: 'paid', start: '2026-07-12', end: '2026-08-31', impressions: 96_400, clicks: 1780, leads: 31 },
  { id: 'test-camp-3', type: 'post', name: 'Palm Jumeirah penthouse', listing: 4, status: 'running', payment: 'paid', start: '2026-07-20', end: '2026-09-01', impressions: 61_800, clicks: 1145, leads: 19 },
  { id: 'test-camp-4', type: 'campaign', name: 'Downtown rentals · retargeting', listing: 2, status: 'pending', payment: 'pending', start: '2026-08-10', end: '2026-09-10', impressions: 0, clicks: 0, leads: 0 },
  { id: 'test-camp-5', type: 'post', name: 'Creek Harbour launch', listing: 3, status: 'pending', payment: 'pending', start: '2026-08-18', end: '2026-09-30', impressions: 0, clicks: 0, leads: 0 },
  { id: 'test-camp-6', type: 'campaign', name: 'Business Bay · lead gen', listing: 6, status: 'finished', payment: 'paid', start: '2026-05-01', end: '2026-06-15', impressions: 212_500, clicks: 4080, leads: 62 },
  { id: 'test-camp-7', type: 'post', name: 'JLT rentals · spring', listing: 7, status: 'finished', payment: 'paid', start: '2026-04-05', end: '2026-05-20', impressions: 88_900, clicks: 1520, leads: 24 },
  { id: 'test-camp-8', type: 'feed', name: 'Dubai Hills open house', listing: 8, status: 'finished', payment: 'failed', start: '2026-03-10', end: '2026-04-10', impressions: 14_300, clicks: 210, leads: 3 },
];

// --- System logs -----------------------------------------------------
// Background activity the brokerage can audit: CRM syncs, imports,
// delivery failures. `t` is an ISO timestamp; levels mirror the filter
// chips on the System logs page.
export interface DemoSystemLog {
  id: string;
  t: string;
  level: 'info' | 'warning' | 'error';
  action: string;
  message: string;
  actor: string | null;
  target: string | null;
}

export const SYSTEM_LOGS: DemoSystemLog[] = [
  { id: 'log-1', t: '2026-08-05T09:14:00.000Z', level: 'info', action: 'listing.sync', message: 'Pixxi feed synced — 46 listings up to date', actor: null, target: 'feed: pixxi' },
  { id: 'log-2', t: '2026-08-05T09:13:40.000Z', level: 'warning', action: 'listing.sync', message: '2 listings skipped — missing permit number', actor: null, target: 'TEST-1032, TEST-1041' },
  { id: 'log-3', t: '2026-08-05T08:02:11.000Z', level: 'info', action: 'agent.created', message: 'Agent added and proptree provisioned', actor: 'Martin Ploug', target: 'Daniel Okafor' },
  { id: 'log-4', t: '2026-08-04T18:45:02.000Z', level: 'error', action: 'lead.webhook', message: 'CRM webhook failed after 3 retries (504)', actor: null, target: 'inquiry test-inquiry-4' },
  { id: 'log-5', t: '2026-08-04T16:20:55.000Z', level: 'info', action: 'form.updated', message: 'Default inquiry form questions changed', actor: 'Martin Ploug', target: 'Default inquiry form' },
  { id: 'log-6', t: '2026-08-04T11:07:19.000Z', level: 'info', action: 'proptree.updated', message: 'Agency proptree buttons reordered', actor: 'Martin Ploug', target: '@yourbrokerage' },
  { id: 'log-7', t: '2026-08-03T22:30:00.000Z', level: 'warning', action: 'billing.invoice', message: 'Card charge retried — payment succeeded on attempt 2', actor: null, target: 'DEMO-0003' },
  { id: 'log-8', t: '2026-08-03T14:12:37.000Z', level: 'info', action: 'affiliate.created', message: 'Affiliate page created', actor: 'Martin Ploug', target: '@property-expo-2026' },
  { id: 'log-9', t: '2026-08-02T07:55:12.000Z', level: 'error', action: 'listing.sync', message: 'Feed unreachable — connection timed out', actor: null, target: 'feed: pixxi' },
  { id: 'log-10', t: '2026-08-01T19:41:08.000Z', level: 'info', action: 'agent.login', message: 'Agent signed in to their proptree', actor: 'Sarah Mitchell', target: 'p7f3k2' },
  { id: 'log-11', t: '2026-08-01T10:03:44.000Z', level: 'info', action: 'listing.sync', message: 'Nightly sync completed — no changes', actor: null, target: 'feed: pixxi' },
  { id: 'log-12', t: '2026-07-31T16:48:20.000Z', level: 'warning', action: 'form.submission', message: 'Submission discarded — failed spam check', actor: null, target: 'Default inquiry form' },
];
